import {
  Component,
  ComponentRef,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { IssueState } from '../../../../models/enums/issueStates';
import { CommActionItem } from '../../../../interfaces/comm-action-item';
import { ActionItemType } from '../../../../models/enums/actionItemType';
import { IssueComponent } from '../../../issue/issue.component';
import { UserstoryService } from '../../../../services/userstory.service';
import { Status } from '../../../../models/status';
import { Statuscategory } from '../../../../models/statuscategory';
import { IssueType } from '../../../../models/enums/issueType';
import { Userstory } from '../../../../models/userstory';
import { SettingsService } from '../../../../services/settings.service';
import { Issue } from '../../../../models/issue';
import { IssueTypeMappingUtil } from '../../../../util/issuetype-mapping-util';
import { ChannelService } from '../../../../services/channel.service';
import { IssuecardComponent } from '../../../issuecard/issuecard.component';
import { IssuecardHelper } from '../../../issuecard/issuecard.helper';
import { DynamicElementService } from '../../../../services/dynamicElement.service';
import { EventIssueOpened } from '../../../../models/syncEvents/persistenceEvents/eventIssueOpened';
import { IssueSynchroHelper } from '../../../issue/issue.synchro.helper';
import { OnlineuserService } from '../../../../services/onlineuser.service';
import { SynchronizationService } from '../../../../services/synchronization.service';
import { HTMLElementPositioningHelper } from '../../../../util/htmlelement-positioning-helper';

@Component({
  selector: 'app-chat-widget-action-item',
  templateUrl: './action-item.component.html',
  styleUrls: ['./action-item.component.scss']
  })
export class ChatWidgetActionItemComponent {
  @ViewChild('newBklIssue', { read: ViewContainerRef }) newBacklogIssueComponentChild: ViewContainerRef;

  @Input() actionItem: CommActionItem;

  @Output() actionItemUpdated: EventEmitter<{ actionItem: CommActionItem; update: Partial<CommActionItem> }> = new EventEmitter();
  @Output() issuecardClosed = new EventEmitter<Issue>();
  @Output() issueRemoved = new EventEmitter<Issue>();



  protected readonly IssueState = IssueState;
  protected readonly ActionItemType = ActionItemType;

  constructor(
    private userstoryService: UserstoryService,
    private settingsService: SettingsService,
    private channelService: ChannelService,
    private issueCardHelper: IssuecardHelper,
    private dynamicElementService: DynamicElementService,
    private onlineUserService: OnlineuserService,
    private sync: SynchronizationService,
    private positionHelper: HTMLElementPositioningHelper,
  ) {
  }

  openNewIssueDialog(event: MouseEvent){
    // Create new Task
    const tempIssueId = this.userstoryService.getTemporaryIdForNewIssue();
    const issueComp: IssueComponent = this.createIssueComponent(tempIssueId);
    // Show the IssueCard modal window
    issueComp.createIssuecardForIssue(event, issueComp.issue, false);
  }


  acceptActionItem() {
    const normalizedType = this.actionItem.issueType.toUpperCase().trim();
    const issueType = normalizedType === 'STORY'
      ? IssueType.USERSTORY
      : IssueType[normalizedType as keyof typeof IssueType];
    this.userstoryService.createIssue(
      this.settingsService.getProjectName(),
      issueType,
      this.actionItem.title,
      this.actionItem.description,
    ).subscribe((story) => {
      console.log(this.actionItem.id);
      this.updateActionItem(this.actionItem.id, {
        state: IssueState.ACCEPTED,
        issueId: story.id,
      });
      if (this.actionItem.parentActionItemId) {
        this.updateActionItem(this.actionItem.parentActionItemId, {
          state: IssueState.REJECTED,
        });
      }
    });
  }

  rejectActionItem(){
    this.updateActionItem(this.actionItem.id, {state: IssueState.REJECTED});
  }

  handleIssueClicked(event: MouseEvent): void {
    this.userstoryService.getParentIssue(this.settingsService.getProjectName(), this.actionItem.issueId).subscribe(
      res => {
        if (this.actionItem) {
          res.summary = this.actionItem.title;
          res.description = this.actionItem.description;
          const issueTypeMap = {
            'STORY': IssueType.USERSTORY,
            'TASK': IssueType.TASK,
            'BUG': IssueType.BUG,
          };
          res.issueType = issueTypeMap[this.actionItem.issueType];
        }
        this.createIssuecardForIssue(event, res, this.actionItem);
      },
    );
  }

  updateExistingIssue(): void {
    this.userstoryService.getParentIssue(this.settingsService.getProjectName(), this.actionItem.issueId).subscribe(
      (currentIssue: Userstory) => {
        currentIssue.summary = this.actionItem.title;
        currentIssue.description = this.actionItem.description;
        this.userstoryService.updateIssue(this.settingsService.getProjectName(), this.actionItem.issueId, currentIssue, false, () => {
          this.updateActionItem(this.actionItem.id, {
            state: IssueState.ACCEPTED,
          });
        },
        );
      },
    );
  }

  private  mapIssueToActionItem(issue: Issue): CommActionItem {
    return {
      title: issue.summary,
      description: issue.description,
      actionItemType: ActionItemType.ACTION_ITEM_NEW,
      issueType: IssueTypeMappingUtil.mapToJiraIssueTypeName(issue.issueType).toUpperCase(),
      timestampProcessed: new Date(),
      state: IssueState.ACCEPTED,
      issueId: issue.id,
    };
  }

  updateActionItem(actionItemId: number | null, update: Partial<CommActionItem>) {
    this.channelService.updateActionItem(actionItemId, update).subscribe(result => {
      this.actionItemUpdated.emit({actionItem: result, update: update});
    });
  }

  private createIssuecardForIssue(event: MouseEvent, issue: Issue, actionItem: CommActionItem, onCreateAssignToSprint = true): void {
    // Dont open a new card if a card for this issue already exists
    if (this.dynamicElementService.containsIssuecardForIssue(issue.id)) {
      return;
    }
    this.sync.sendPersistenceEventNew(new EventIssueOpened(issue.id, this.onlineUserService.getAvatarUrl()));

    // Update last viewed timestamp when card is opened
    this.userstoryService.setLastViewed(this.settingsService.getProjectName(), issue.id).subscribe();
    issue.lastViewedInAwall = new Date().toISOString();

    // Create a new card and open it at the correct position
    const card: IssuecardComponent = this.dynamicallyAddIssueCardComponent(issue, '', actionItem, onCreateAssignToSprint);
    const cardElement = card.getElementRef().nativeElement;
    const pos = this.positionHelper.getPositionForNewCard(event, cardElement);
    this.positionHelper.setAbsolutePosition(cardElement, pos.x, pos.y);
    IssueSynchroHelper.sendIssueCardOpenedEvent(this.sync, card, this.positionHelper, this.onlineUserService);
  }

  private createIssueComponent(issueId: number): IssueComponent {
    // Create new Task
    const statusTodo: Status = new Status('', '', 10000, 'To Do', new Statuscategory('blue-gray', 2, 'new'));
    const _type = this.actionItem?.issueType?.toUpperCase() || '';
    const issueType = IssueType[_type as keyof typeof IssueType] ?? IssueType.USERSTORY;

    // const newIssue: Userstory = new Userstory(null, '', undefined, issueId, null, null, title || '',
    //   1, null, statusTodo, [], [], null, null, null, false, null, null, []);
    const newIssue: Userstory = new Userstory(
      null,
      this.actionItem?.description || '',
      undefined,
      issueId,
      null,
      null,
      this.actionItem?.title,
      issueType || 1,
      null,
      statusTodo,
      [],
      [],
      null,
      null,
      null,
      false,
      null,
      null,
      null,
      [],
    );

    // Dynamically create a new Issue in the row
    const componentRef: ComponentRef<IssueComponent> = this.newBacklogIssueComponentChild.createComponent(IssueComponent);
    const issue: IssueComponent = componentRef.instance;
    issue.issue = newIssue;
    issue.projectKey = this.settingsService.getProjectName();
    issue.componentRef = componentRef;

    componentRef.instance.issueCreated.subscribe(createdIssue => {
      const mappedActionItem = this.mapIssueToActionItem(createdIssue);
      console.log('****CALLED IN CREATEISSUECOMPONENT', mappedActionItem);
      this.updateActionItem(this.actionItem.id, mappedActionItem);
      if (this.actionItem.parentActionItemId){
        this.updateActionItem(this.actionItem.parentActionItemId, { state: IssueState.REJECTED});
      }
    });
    return issue;
  }

  private dynamicallyAddIssueCardComponent(
    issue: Issue,
    avatar = '',
    actionItem: CommActionItem,
    onCreateAssignToSprint = true):
    IssuecardComponent {
    const injector = Injector.create({
      providers: [
        { provide: 'issue', useValue: issue },
        { provide: 'projectKey', useValue: this.settingsService.getProjectName() },
        { provide: 'statuses', useValue: [] },
        { provide: 'avatar', useValue: avatar },
        {
          provide: 'onCloseCallback', useValue: (issueCard) => {
            this.issueCardHelper.removeCardFromGlobalReferences(issueCard);
            if (issueCard) {
              issueCard.componentRef.destroy();
            }
            this.issuecardClosed.emit(issue);
          },
        },
        {
          provide: 'onNewIssueCreatedCallback', useValue: () => {
          },
        },
        {
          provide: 'onNewSubIssueCreatedCallback', useValue: () => {
          },
        },
        {
          provide: 'onIssueRemovedCallback', useValue: (issueId) => {
            // this.deleteIssueFromMessage(issueId); //TODO: add logic for deleting it!
            this.issueRemoved.emit(issueId);
          },
        },
        {
          provide: 'onSubIssueRemovedCallback', useValue: () => {
          },
        },
        {
          provide: 'onIssueModified', useValue: () => {
            actionItem = { ...actionItem, state: IssueState.ACCEPTED};
            this.updateActionItem(actionItem.id, actionItem);
          },
        },
      ],
    });

    const insertionPoint: ViewContainerRef = this.dynamicElementService.getInsertionPoint();
    const componentRef: ComponentRef<IssuecardComponent> = insertionPoint.createComponent(IssuecardComponent, { injector: injector });
    const card: IssuecardComponent = componentRef.instance;
    card.componentRef = componentRef;
    card.onCreateAssignToSprint = onCreateAssignToSprint;

    this.issueCardHelper.setupIssueCardAsDraggable(card);
    this.issueCardHelper.setupIssueCardAsClickable(card);
    this.dynamicElementService.insert(componentRef);
    return card;
  }



}
